import {
  IAolBidRequest,
  IAppNexusBidRequest,
  IBidRequest,
  ICriteoBidRequest,
  IEmxDigitalBidRequest,
  IFacebookBidRequest,
  IGumGumBidRequest,
  IIXBidRequest,
  IOpenxBidRequest,
  IPubmaticBidRequest,
  IRoundelBidRequest,
  IRubiconBidRequest,
  ISovrnBidRequest,
  ITripleliftBidRequest,
  IUnrulyBidRequest,
  IS2SBidRequest,
  IYieldmoBidRequest
} from '../../typings/IPrebid'
import { DEFAULT_FLOOR, PENNY_PRECISION } from '../constants'
import { Bidder } from '../enums'
import { getQueryParam } from '../helpers/getQueryParam'
import { roundNumberToPrecision } from '../helpers/numbers'
import WebModel from '../models/WebModel'

/** This class houses all prebid bid request changes
 * occurring client-side. To add params to a specific request,
 * append the bidder's BidRequest interface in prebid.d.ts.
 * Then, find the bidders adjustment func below and add the param.
 */
export class BidRequestAdjustments implements IBidRequestAdjustment {
  readonly floor: number
  constructor(readonly model: WebModel, readonly adUnitId: string) {
    this.floor = getFloor(this.adUnitId, this.model)
  }

  adjustBids(bids: Array<IBidRequest>): Array<IBidRequest> {
    return bids.map(this.adjust.bind(this))
  }

  adjust(bid: IBidRequest): IBidRequest {
    if (this[bid.bidder]) {
      const func: IBidRequestAdjustFunc = this[bid.bidder].bind(this)
      func(bid)
    }

    return bid
  }

  getDiscrepancy(bidder: Bidder): number {
    return (this.model.discrepancies && this.model.discrepancies[bidder]) || 1
  }

  aol(bid: IAolBidRequest): void {
    const aolGrossAdjustment = 0.88
    const aolDiscrepancy = this.getDiscrepancy(Bidder.aol)
    bid.params.bidFloor = roundNumberToPrecision(
      this.floor / aolGrossAdjustment / aolDiscrepancy,
      PENNY_PRECISION
    )
  }
  [Bidder.uam](bid: IBidRequest): void {
    /* noop */
  }

  [Bidder.roundel](bid: IRoundelBidRequest): void {
    const roundelDiscrepancy = this.getDiscrepancy(Bidder.roundel)
    bid.params.bidFloor = roundNumberToPrecision(
      this.floor / roundelDiscrepancy,
      2
    )
    bid.params.bidFloorCur = 'USD'
  }

  [Bidder.appnexus](bid: IAppNexusBidRequest): void {
    const appNexusGrossAdjustment = 0.85
    const appNexusDiscrepancy = this.getDiscrepancy(Bidder.appnexus)
    bid.params.reserve = roundNumberToPrecision(
      this.floor / appNexusGrossAdjustment / appNexusDiscrepancy,
      PENNY_PRECISION
    )
  }
  [Bidder.appnexusAst](bid: IAppNexusBidRequest): void {
    /* noop */
  }

  [Bidder.emx](bid: IEmxDigitalBidRequest): void {
    const emxDiscrepancy = this.getDiscrepancy(Bidder.emx)
    bid.params.bidfloor = roundNumberToPrecision(
      this.floor / emxDiscrepancy,
      2
    ).toString()
  }

  [Bidder.audienceNetwork](bid: IFacebookBidRequest): void {
    const facebookDiscrepency = this.getDiscrepancy(Bidder.audienceNetwork)
    bid.params.bidFloor = roundNumberToPrecision(
      this.floor / facebookDiscrepency,
      2
    )
  }

  [Bidder.criteo](bid: ICriteoBidRequest): void {
    /* noop */
  }
  [Bidder.indexExchange](bid: IIXBidRequest): void {
    const indexExchangeDiscrepancy = this.getDiscrepancy(Bidder.indexExchange)
    bid.params.bidFloor = roundNumberToPrecision(
      this.floor / indexExchangeDiscrepancy,
      2
    )
    bid.params.bidFloorCur = 'USD'
  }

  [Bidder.gumgum](bid: IGumGumBidRequest): void {
    bid.params.bidfloor = this.floor
  }
  [Bidder.gumgum_icv](bid: IGumGumBidRequest): void {
    bid.params.bidfloor = this.floor
  }
  [Bidder.google](bid: IBidRequest): void {
    /* noop */
  }
  [Bidder.openx](bid: IOpenxBidRequest): void {
    const openxDiscrepancy = this.getDiscrepancy(Bidder.openx)
    // We don't know why this is needed but it increases our OpenX responses
    const oddAdjustment = 0.01
    bid.params.customFloor =
      roundNumberToPrecision(this.floor / openxDiscrepancy, 2) + oddAdjustment
  }
  [Bidder.pubmatic](bid: IPubmaticBidRequest): void {
    const pubmaticDiscrepancy = this.getDiscrepancy(Bidder.pubmatic)
    bid.params.kadfloor = roundNumberToPrecision(
      this.floor / pubmaticDiscrepancy,
      2
    ).toString()
  }
  [Bidder.sovrn](bid: ISovrnBidRequest): void {
    const sovrnDiscrepancy = this.getDiscrepancy(Bidder.sovrn)
    bid.params.bidfloor = roundNumberToPrecision(
      this.floor / sovrnDiscrepancy,
      2
    )
  }
  [Bidder.rubicon](bid: IRubiconBidRequest): void {
    const rubiconGrossAdjustment = 0.865
    const rubiconDiscrepancy = this.getDiscrepancy(Bidder.rubicon)
    bid.params.floor = roundNumberToPrecision(
      this.floor / rubiconGrossAdjustment / rubiconDiscrepancy,
      2
    )
  }
  [Bidder.triplelift](bid: ITripleliftBidRequest): void {
    const tripleliftDiscrepancy = this.getDiscrepancy(Bidder.triplelift)
    if (
      bid.params.inventoryCode &&
      bid.params.inventoryCode.indexOf('video') === -1
    ) {
      bid.params.floor = roundNumberToPrecision(
        this.floor / tripleliftDiscrepancy,
        2
      )
    }
  }

  [Bidder.unruly](bid: IUnrulyBidRequest): void {
    if (getQueryParam('test') === Bidder.unruly) {
      bid.params.targetingUUID = '6f15e139-5f18-49a1-b52f-87e5e69ee65e'
      bid.params.siteId = 1081534
    }
  }

  [Bidder.yieldmo](bid: IYieldmoBidRequest): void {
    const yieldmoDiscrepency = this.getDiscrepancy(Bidder.yieldmo)
    bid.params.bidFloor = roundNumberToPrecision(
      this.floor / yieldmoDiscrepency,
      2
    )
  }
  [Bidder.S2S](bid: IS2SBidRequest): void {
    bid.params.floor = this.floor
    bid.params.iabOptouts = this.model.iabOptouts
  }
}

export function getFloor(adUnitId: string, model: WebModel): number {
  /**
   *  Enable the below code to begin dynamic flooring.
   *  While disabled, the hard-coded DEFAULT_FLOOR is used instead.
   */
  // const { baseFloor, absoluteFloor, cpmIndices } = model
  // if (!baseFloor || !absoluteFloor || !cpmIndices) {
  //   return DEFAULT_FLOOR
  // }

  // const floor = baseFloor * (cpmIndices[adUnitId] || 1)

  // if (floor >= absoluteFloor) {
  //   return floor
  // }
  // if (absoluteFloor > floor) {
  //   return absoluteFloor
  // }

  return DEFAULT_FLOOR(model.flooring)
}

type IBidRequestAdjustment = { [key in Bidder]: IBidRequestAdjustFunc }

type IBidRequestAdjustFunc = (bid: IBidRequest) => void
